import React, { useEffect, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import notificationService from './services/notificationService';

// Auth Pages
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import AdminPage from './pages/AdminPage';
import CourseSelectionPage from './pages/CourseSelectionPage';

// Main Pages
import HomePage from './pages/HomePage';
import TracksPage from './pages/TracksPage';
import TrackDetailPage from './pages/TrackDetailPage';
import LessonPage from './pages/LessonPage';
import PhrasesPage from './pages/PhrasesPage';
import ReviewPage from './pages/ReviewPage';
import AchievementsPage from './pages/AchievementsPage';
import ProfilePage from './pages/ProfilePage';

// Feature Pages
import AIChatPage from './pages/AIChatPage';
import StatsPage from './pages/StatsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import GamesPage from './pages/GamesPage';
import MemoryGamePage from './pages/MemoryGamePage';
import SpeedQuizPage from './pages/SpeedQuizPage';
import WritingPage from './pages/WritingPage';
import FlashcardsPage from './pages/FlashcardsPage';

// Error Boundary to catch JavaScript errors and prevent white screen
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error Boundary caught error:', error, errorInfo);
  }

  handleRetry = () => {
    // Clear any potentially corrupted storage data
    try {
      localStorage.removeItem('fuse_auth_token');
      localStorage.removeItem('fuse_user_data');
    } catch (e) {
      console.warn('Could not clear localStorage');
    }
    try {
      sessionStorage.removeItem('fuse_auth_token');
      sessionStorage.removeItem('fuse_user_data');
    } catch (e) {
      console.warn('Could not clear sessionStorage');
    }
    // Reload the page
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Ops! Algo deu errado</h2>
            <p className="text-slate-400 mb-6">
              Parece que houve um problema ao carregar o aplicativo. Isso pode acontecer em alguns navegadores com configurações de privacidade restritas.
            </p>
            <button
              onClick={this.handleRetry}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-medium rounded-xl transition-all"
            >
              Tentar novamente
            </button>
            <p className="text-xs text-slate-500 mt-4">
              Dica: Tente abrir o link diretamente no Safari ou Chrome ao invés de dentro de outros aplicativos.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// App Router Component - handles session_id detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check query params for session_id (OAuth callback)
  if (location.search?.includes('session_id=')) {
    return <LandingPage />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Course Selection Route */}
      <Route path="/select-course" element={
        <ProtectedRoute>
          <CourseSelectionPage />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      } />
      <Route path="/tracks" element={
        <ProtectedRoute>
          <TracksPage />
        </ProtectedRoute>
      } />
      <Route path="/track/:trackId" element={
        <ProtectedRoute>
          <TrackDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/lesson/:trackId/:lessonId" element={
        <ProtectedRoute>
          <LessonPage />
        </ProtectedRoute>
      } />
      <Route path="/phrases" element={
        <ProtectedRoute>
          <PhrasesPage />
        </ProtectedRoute>
      } />
      <Route path="/review" element={
        <ProtectedRoute>
          <ReviewPage />
        </ProtectedRoute>
      } />
      <Route path="/achievements" element={
        <ProtectedRoute>
          <AchievementsPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Feature Routes */}
      <Route path="/chat" element={
        <ProtectedRoute>
          <AIChatPage />
        </ProtectedRoute>
      } />
      <Route path="/stats" element={
        <ProtectedRoute>
          <StatsPage />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <LeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/games" element={
        <ProtectedRoute>
          <GamesPage />
        </ProtectedRoute>
      } />
      <Route path="/games/memory" element={
        <ProtectedRoute>
          <MemoryGamePage />
        </ProtectedRoute>
      } />
      <Route path="/games/speed-quiz" element={
        <ProtectedRoute>
          <SpeedQuizPage />
        </ProtectedRoute>
      } />
      <Route path="/writing" element={
        <ProtectedRoute>
          <WritingPage />
        </ProtectedRoute>
      } />
      <Route path="/flashcards" element={
        <ProtectedRoute>
          <FlashcardsPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Route */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  // Initialize notification service on app load
  useEffect(() => {
    try {
      notificationService.initialize();
    } catch (e) {
      console.warn('Failed to initialize notifications:', e);
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppProvider>
          <Router>
            <div className="App min-h-screen bg-background">
              <AppRouter />
              <Toaster />
            </div>
          </Router>
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
